import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2, ShieldCheck, Smartphone, ArrowLeft, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth";
import { LOGO } from "@/lib/brand";
import { toast } from "sonner";

const formatErr = (detail) => {
  if (!detail) return "Accesso non riuscito. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

const CARD_MOTION = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };

const Label = ({ children }) => <label className="text-xs font-bold uppercase tracking-wider text-slate-400">{children}</label>;

function CredentialsStep({ onNext }) {
  const { signIn } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      onNext(await signIn(identifier.trim(), password));
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="login-password-form">
      <div>
        <Label>Nome e cognome</Label>
        <Input data-testid="login-identifier" required value={identifier} autoComplete="username"
          onChange={(e) => setIdentifier(e.target.value)} placeholder="Nome Cognome"
          className="mt-1 bg-slate-950 border-white/10 text-white" />
      </div>
      <div>
        <Label>Password</Label>
        <Input data-testid="login-password" type="password" required value={password} autoComplete="current-password"
          onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
          className="mt-1 bg-slate-950 border-white/10 text-white" />
      </div>
      <Button data-testid="login-submit" type="submit" disabled={loading}
        className="w-full h-11 bg-accent hover:bg-accent/90 text-white gap-2">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Continua
      </Button>
    </form>
  );
}

function CodeStep({ step, onBack }) {
  const { confirmTotp } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const isSetup = !!step.setup_required;

  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await confirmTotp(step.mfa_token, code);
      toast.success(isSetup ? "Google Authenticator attivato. Benvenuto!" : `Benvenuto ${user.name || ""}`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
      setCode("");
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="login-totp-form">
      {isSetup ? (
        <div className="space-y-3" data-testid="totp-setup">
          <p className="text-sm text-slate-300">
            <span className="font-semibold text-white">Primo accesso:</span> apri <span className="text-white">Google Authenticator</span>,
            tocca <span className="text-white">+</span> e inquadra il QR. Comparirà la voce <span className="text-white">"Leadders CRM: {step.user?.name}"</span>.
          </p>
          <div className="flex justify-center">
            <img src={step.qr} alt="QR Google Authenticator" data-testid="totp-qr" className="rounded-lg bg-white p-2 w-44 h-44" />
          </div>
          <button type="button" data-testid="totp-copy-secret"
            onClick={() => { navigator.clipboard?.writeText(step.secret); toast.success("Chiave copiata"); }}
            className="w-full flex items-center justify-center gap-2 text-[11px] text-slate-500 hover:text-slate-300 font-mono">
            <Copy className="h-3 w-3" /> {step.secret}
          </button>
        </div>
      ) : (
        <p className="text-sm text-slate-300">Ciao <span className="text-white font-semibold">{step.user?.name}</span>, inserisci il codice a 6 cifre di Google Authenticator.</p>
      )}
      <div>
        <Label>Codice Authenticator</Label>
        <Input ref={inputRef} data-testid="login-totp-code" inputMode="numeric" autoComplete="one-time-code" required
          value={code} maxLength={7} onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, ""))} placeholder="000 000"
          className="mt-1 bg-slate-950 border-white/10 text-white text-center text-2xl tracking-[0.4em] font-mono h-14" />
      </div>
      <Button data-testid="login-totp-submit" type="submit" disabled={loading || code.replace(/\s/g, "").length !== 6}
        className="w-full h-11 bg-accent hover:bg-accent/90 text-white gap-2">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" />} {isSetup ? "Attiva e accedi" : "Accedi"}
      </Button>
      <button type="button" data-testid="login-totp-back" onClick={onBack}
        className="w-full flex items-center justify-center gap-1 text-xs text-slate-500 hover:text-slate-300">
        <ArrowLeft className="h-3 w-3" /> Torna indietro
      </button>
    </form>
  );
}

export default function Login() {
  const { user, ready } = useAuth();
  const [step, setStep] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (ready && user) navigate("/dashboard", { replace: true });
  }, [ready, user, navigate]);

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 grain">
      <motion.div {...CARD_MOTION}
        className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src={LOGO} alt="Leadders" className="h-10 w-auto brightness-0 invert" />
        </div>
        <div className="bg-slate-900 border border-white/10 rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center gap-2 mb-1">
            <Lock className="h-5 w-5 text-accent" />
            <h1 className="font-display text-xl font-bold text-white">Area Consulente</h1>
          </div>
          <p className="text-sm text-slate-400 mb-6">
            {step ? "Verifica in due passaggi" : "Accesso riservato: nome e cognome, password e codice Google Authenticator."}
          </p>
          {step ? <CodeStep step={step} onBack={() => setStep(null)} /> : <CredentialsStep onNext={setStep} />}
        </div>
        <p className="text-center text-xs text-slate-600 mt-6">
          Leadders · CRM protetto con autenticazione a due fattori · GDPR UE 2016/679
        </p>
      </motion.div>
    </div>
  );
}
